import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../common/Header';
import SideMenu from '../common/SideMenu';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import './LotDetail.css';
import {
  createShipLot,
  deleteShipLot,
  fetchShipLot,
  fetchShipments,
  updateShipLot,
  isPersonalInfoRestricted,
  fetchLotWmsLogs,
  resyncEntryOrder,
  syncDeliveryOrder,
  cancelDeliveryOrder,
  downloadLotLocations,
  downloadLotInvoice,
  downloadLotPackingList,
  downloadLotPurchaseDetail,
} from '../../../utils/apiClient';

const DESTINATION_OPTIONS = [
  { value: 'cn', label: '中国' },
];

const DEFAULT_COUNTRY_LABEL = DESTINATION_OPTIONS[0]?.label ?? '中国';

const STATUS_OPTIONS = [
  { value: 'shipped', labelKey: 'lot.statuses.shipped' },
  { value: 'in_progress', labelKey: 'lot.statuses.in_progress' },
  { value: 'sorted', labelKey: 'lot.statuses.sorted' },
  { value: 'ready', labelKey: 'lot.statuses.ready' },
  { value: 'issue', labelKey: 'lot.statuses.issue' },
  { value: 'hold', labelKey: 'lot.statuses.hold' },
];

const SHIPMENT_STATUS_META = {
  1: { tone: 'danger', labelKey: 'ship.status1' },
  2: { tone: 'info', labelKey: 'ship.status2' },
  3: { tone: 'muted', labelKey: 'ship.status3' },
  4: { tone: 'muted', labelKey: 'ship.status4' },
  5: { tone: 'muted', labelKey: 'ship.status5' },
  6: { tone: 'muted', labelKey: 'ship.status6' },
  7: { tone: 'success', labelKey: 'ship.status7' },
};

const WMS_STATUS_META = {
  null: { tone: 'muted', labelKey: 'lotDetail.wmsOutbound.unsent' },
  1:    { tone: 'info', labelKey: 'lotDetail.wmsOutbound.sent' },
  7:    { tone: 'success', labelKey: 'lotDetail.wmsOutbound.confirmed' },
};

const ALERT_META = {
  normal: { tone: 'muted', labelKey: 'lot.alerts.normal' },
  notice: { tone: 'info', labelKey: 'lot.alerts.notice' },
  warning: { tone: 'warning', labelKey: 'lot.alerts.warning' },
};

const LOT_TEMPLATES = {
  default: {
    lotNumber: 'L-10000',
    destination: 'cn',
    status: 'in_progress',
    trackingNumber: '',
    grossWeightKg: '',
    netWeightKg: '',
    shipmentIds: [],
  },
  new: {
    lotNumber: '',
    destination: 'cn',
    status: 'in_progress',
    trackingNumber: '',
    locationsOrderNumber: '',
    grossWeightKg: '',
    netWeightKg: '',
    shipmentIds: [],
  },
  'L-10002': {
    lotNumber: 'L-10002',
    destination: 'cn',
    status: 'in_progress',
    trackingNumber: '',
    grossWeightKg: '',
    netWeightKg: '',
    shipmentIds: [],
  },
};

const PAGE_SIZE = 5;
const DEFAULT_SHIPMENT_STATUSES = [1, 2];

const buildShipmentQuery = (statuses = DEFAULT_SHIPMENT_STATUSES) => {
  const params = new URLSearchParams();
  statuses
    .map((status) => Number(status))
    .filter((status) => Number.isFinite(status))
    .forEach((status, index) => {
      params.append(`status[${index}]`, status);
    });
  return params.toString();
};

const tryParseJsonObject = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch (error) {
    return null;
  }
};

const cleanTextValue = (value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text.length ? text : null;
};

const mergeUniqueParts = (values) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const cleaned = cleanTextValue(value);
    if (!cleaned || seen.has(cleaned)) return;
    seen.add(cleaned);
    result.push(cleaned);
  });
  return result;
};

const toAddressObject = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') return tryParseJsonObject(value);
  return null;
};

const extractPostalCode = (rawAddress) => {
  if (!rawAddress) return null;
  if (typeof rawAddress === 'string') {
    const parsed = tryParseJsonObject(rawAddress);
    if (parsed) return extractPostalCode(parsed);
    return null;
  }
  if (typeof rawAddress === 'object') {
    return cleanTextValue(
      rawAddress.postal_code
      ?? rawAddress.postalCode
      ?? rawAddress.zip
      ?? rawAddress.zip_code,
    );
  }
  return null;
};

const parseShipmentStatusCode = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = cleanTextValue(value);
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const getShipmentStatusMeta = (status) => {
  const code = parseShipmentStatusCode(status);
  if (code === null) return null;
  return SHIPMENT_STATUS_META[code] ?? null;
};

const formatPrefecture = (prefecture) => {
  if (!prefecture) return null;

  const resolveParts = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return null;
    const {
      province,
      state,
      region,
      prefecture: prefectureName,
      area,
      city,
      city_name: cityName,
      district,
      district_name: districtName,
      name,
    } = candidate;
    const parts = mergeUniqueParts([
      province ?? state ?? region ?? prefectureName ?? area,
      city ?? cityName ?? district ?? districtName,
      name,
    ]);
    return parts.length ? parts.join(' ') : null;
  };

  const parsedCandidate = toAddressObject(prefecture);
  if (parsedCandidate) {
    const formatted = resolveParts(parsedCandidate);
    if (formatted) return formatted;
  }

  return cleanTextValue(prefecture);
};

const formatCountry = (rawAddress, fallbackCountry, defaultCountryLabel = '-') => {
  const inspectAddressObject = (address) => {
    if (!address || typeof address !== 'object') return null;
    const direct = cleanTextValue(
      address.country
      ?? address.countryName
      ?? address.country_name,
    );
    if (direct) return direct;

    const nestedPrefecture = typeof address.prefecture === 'string'
      ? tryParseJsonObject(address.prefecture)
      : address.prefecture;
    if (nestedPrefecture && typeof nestedPrefecture === 'object') {
      const nestedCountry = cleanTextValue(
        nestedPrefecture.country
        ?? nestedPrefecture.countryName
        ?? nestedPrefecture.country_name,
      );
      if (nestedCountry) return nestedCountry;
    }

    return null;
  };

  if (typeof rawAddress === 'string') {
    const parsed = tryParseJsonObject(rawAddress);
    if (parsed) {
      return formatCountry(parsed, fallbackCountry, defaultCountryLabel);
    }
  }

  if (typeof rawAddress === 'object' && rawAddress !== null) {
    const extracted = inspectAddressObject(rawAddress);
    if (extracted) return extracted;
  }

  const normalizedFallback = cleanTextValue(fallbackCountry);
  const normalizedDefault = cleanTextValue(defaultCountryLabel) ?? '-';
  return normalizedFallback ?? normalizedDefault;
};

const formatAddress = (rawAddress) => {
  if (!rawAddress) return '-';

  if (typeof rawAddress === 'string') {
    const parsed = tryParseJsonObject(rawAddress);
    if (parsed) {
      return formatAddress(parsed);
    }
    return rawAddress;
  }

  if (typeof rawAddress === 'object') {
    const prefectureRaw = rawAddress.prefecture;
    const prefectureObject = toAddressObject(prefectureRaw);
    const primaryAddress = cleanTextValue(
      rawAddress.address
      ?? rawAddress.street
      ?? rawAddress.street_address
      ?? prefectureObject?.address,
    );
    const secondaryAddress = cleanTextValue(
      rawAddress.address2
      ?? rawAddress.apartment
      ?? rawAddress.unit,
    );

    if (primaryAddress) {
      return secondaryAddress ? `${primaryAddress} ${secondaryAddress}` : primaryAddress;
    }

    const postalCode = cleanTextValue(rawAddress.postal_code ?? rawAddress.zip);
    const formattedPrefecture = formatPrefecture(prefectureRaw);
    const city = cleanTextValue(rawAddress.city ?? rawAddress.city_name);
    const country = cleanTextValue(rawAddress.country);

    const parts = mergeUniqueParts([postalCode, formattedPrefecture, city, secondaryAddress, country]);
    return parts.length ? parts.join(' ') : '-';
  }

  return '-';
};

const normalizeLotShipments = (shipments, options = {}) => {
  const { personalInfoRestricted = false } = options;

  if (!Array.isArray(shipments)) return [];

  return shipments
    .map((shipment) => {
      const id = shipment?.id ?? shipment?.shipment_id;
      if (!id) return null;

      const cardCountCandidate = Number(
        shipment?.cardCount
        ?? shipment?.card_count
        ?? shipment?.cardsCount,
      );

      const address = shipment?.clientAddress ?? shipment?.client_address ?? null;
      const postalCode = cleanTextValue(
        shipment?.postalCode
        ?? shipment?.postal_code
        ?? shipment?.clientPostalCode,
      );
      const country = cleanTextValue(
        shipment?.country
        ?? shipment?.clientCountry
        ?? shipment?.client_country,
      );

      const normalizedName = cleanTextValue(shipment?.clientName ?? shipment?.client_name) ?? '-';
      const normalizedAddress = formatAddress(address);
      const normalizedPostal = postalCode ?? '-';
      const normalizedCountry = country ?? null;
      const trackingNumber = cleanTextValue(
        shipment?.trackingNumber
        ?? shipment?.tracking_number
        ?? shipment?.wmsReferenceId
        ?? shipment?.wms_reference_id,
      );
      const cnTrackingNumber = cleanTextValue(
        shipment?.cnTrackingNumber
        ?? shipment?.cn_tracking_number
        ?? shipment?.single_tracking_number,
      );

      return {
        id: String(id),
        name: personalInfoRestricted ? '***' : normalizedName,
        addressJa: personalInfoRestricted ? '***' : normalizedAddress,
        postalCode: personalInfoRestricted ? '***' : normalizedPostal,
        country: personalInfoRestricted ? '***' : normalizedCountry,
        cardCount: Number.isFinite(cardCountCandidate) ? cardCountCandidate : null,
        status: parseShipmentStatusCode(
          shipment?.status
          ?? shipment?.statusId
          ?? shipment?.status_id
          ?? shipment?.shipment_status,
        ),
        trackingNumber: trackingNumber ?? null,
        cnTrackingNumber: cnTrackingNumber ?? null,
        splitFromShipmentId: shipment?.splitFromShipmentId ?? shipment?.split_from_shipment_id ?? null,
        splitShipmentIds: shipment?.splitShipmentIds ?? shipment?.split_shipment_ids ?? [],
      };
    })
    .filter(Boolean);
};

const normalizeLotPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const shipments = Array.isArray(payload.shipments) ? payload.shipments : [];

  return {
    lotNumber: payload.lotId ?? payload.lot_id ?? '',
    destination: payload.destinationCode ?? payload.destination_code ?? 'cn',
    status: payload.status ?? 'in_progress',
    wmsStatus: payload.wmsStatus ?? payload.wms_status ?? null,
    alert: payload.alert ?? payload.alert_level ?? 'normal',
    wmsReferenceId: payload.wmsReferenceId ?? payload.wms_reference_id ?? null,
    trackingNumber: payload.trackingNumber
      ?? payload.tracking_number
      ?? payload.wmsReferenceId
      ?? payload.wms_reference_id
      ?? null,
    entryOrderStatus: payload.entryOrderStatus ?? payload.entry_order_status ?? null,
    locationsOrderNumber: payload.locationsOrderNumber ?? payload.locations_order_number ?? '',
    grossWeightKg: payload.grossWeightKg ?? payload.gross_weight_kg ?? null,
    netWeightKg: payload.netWeightKg ?? payload.net_weight_kg ?? null,
    packingListNumber: payload.packingListNumber ?? payload.packing_list_number ?? null,
    createdAt: payload.createdAt ?? payload.created_at ?? null,
    shipmentIds: shipments
      .map((shipment) => shipment?.id ?? shipment?.shipment_id)
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value)),
    shipments,
  };
};

const safeReadJson = async (response) => {
  if (!response || typeof response.json !== 'function') return null;
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const LotDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, lotId } = useParams();
  const isNew = !lotId || lotId === 'new';
  const [shipmentDirectorySource, setShipmentDirectorySource] = useState([]);
  const [lotShipmentRecords, setLotShipmentRecords] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipmentsError, setShipmentsError] = useState(null);
  const [lotLoading, setLotLoading] = useState(!isNew);
  const [lotError, setLotError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resyncingEntry, setResyncingEntry] = useState(false);
  const [syncingDelivery, setSyncingDelivery] = useState(false);
  const [cancelingDelivery, setCancelingDelivery] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [lotMeta, setLotMeta] = useState({
     wmsStatus: null,
     alert: 'normal',
     trackingNumber: null,
     entryOrderStatus: null,
     createdAt: null,
  });
  const [personalInfoRestricted, setPersonalInfoRestricted] = useState(false);
  const [restrictionMeta, setRestrictionMeta] = useState(null);

  // ロット追加/分離ルール表示用 (Lot.jsx と同じロジック)
  const lotConstraints = useMemo(
    () => restrictionMeta?.lot_constraints ?? restrictionMeta?.lotConstraints ?? {},
    [restrictionMeta]
  );
  const guidelineNumbers = useMemo(() => {
    const coerceNumber = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    return {
      maxCardsPerLot: coerceNumber(lotConstraints?.maxCardsPerLot),
      splitWindowDays: coerceNumber(lotConstraints?.splitWindowDays),
      maxRangeSpan: coerceNumber(lotConstraints?.maxRangeSpan),
    };
  }, [lotConstraints]);
  const formatGuidelineNumber = useCallback((value) => {
    if (value === null || value === undefined) return null;
    return value.toLocaleString();
  }, []);
  // 365 日以上は実質「期間制限なし」 (LOT_SPLIT_WINDOW_DAYS=9999 等への対応)
  const isUnlimitedDays = useCallback((value) => {
    if (value === null || value === undefined) return false;
    return Number(value) >= 365;
  }, []);

  const [validationErrors, setValidationErrors] = useState({});
  const validationSummary = useMemo(() => {
    if (!validationErrors || typeof validationErrors !== 'object') return [];
    return Object.values(validationErrors)
      .flatMap((messages) => (Array.isArray(messages) ? messages : [messages]))
      .filter((message) => typeof message === 'string' && message.length > 0);
  }, [validationErrors]);
  const getFieldErrors = useCallback((field) => {
    if (!validationErrors || typeof validationErrors !== 'object') return [];
    const direct = Array.isArray(validationErrors[field]) ? validationErrors[field] : [];
    const nested = Object.entries(validationErrors)
      .filter(([key]) => key.startsWith(`${field}.`))
      .flatMap(([, messages]) => (Array.isArray(messages) ? messages : []));
    return [...direct, ...nested].filter(Boolean);
  }, [validationErrors]);
  const renderFieldError = useCallback((field) => {
    const messages = getFieldErrors(field);
    if (!messages.length) {
      return null;
    }
    return (
      <p className="lot-detail-field-error">
        {messages[0]}
      </p>
    );
  }, [getFieldErrors]);

  const shipmentDirectory = useMemo(() => {
    const map = new Map();

    shipmentDirectorySource.forEach((record) => {
      if (!record?.id) return;
      map.set(record.id, record);
    });

    lotShipmentRecords.forEach((record) => {
      if (!record?.id) return;
      const existing = map.get(record.id) ?? {};
      map.set(record.id, {
        ...existing,
        ...record,
        cardCount: record.cardCount ?? existing.cardCount ?? null,
      });
    });

    const merged = Array.from(map.values());
    if (!personalInfoRestricted) {
      return merged;
    }
    return merged.map((record) => ({
      ...record,
      name: '***',
      addressJa: '***',
      postalCode: '***',
      country: '***',
    }));
  }, [shipmentDirectorySource, lotShipmentRecords, personalInfoRestricted]);

  const renderShipmentStatus = (value) => {
    const meta = getShipmentStatusMeta(value);
    if (!meta) return '-';
    return (
      <span className={`lot-detail-status-badge tone-${meta.tone}`}>
        {t(meta.labelKey)}
      </span>
    );
  };

  const renderWmsStatus = (value) => {
    const code = parseShipmentStatusCode(value);
    const key = code === null ? 'null' : String(code);
    const meta = WMS_STATUS_META[key];
    if (!meta) {
      const fallback = WMS_STATUS_META['null'];
      return (
        <span className={`lot-detail-status-badge tone-${fallback.tone}`}>
          {t(fallback.labelKey)}
        </span>
      );
    }
    return (
      <span className={`lot-detail-status-badge tone-${meta.tone}`}>
        {t(meta.labelKey)}
      </span>
    );
  };

  const renderAlertBadge = (value) => {
    if (!value) return null;
    const meta = ALERT_META[value];
    if (!meta) return null;
    return (
      <span className={`lot-detail-status-badge tone-${meta.tone}`}>
        {t(meta.labelKey)}
      </span>
    );
  };

  // 発送分割: 「611 から分割」「分割あり → 620」「保留中」を発送IDの下に出す
  const renderSplitBadge = (record) => {
    if (!record) return null;
    const lines = [];
    if (record.splitFromShipmentId) {
      lines.push(t('lotDetail.split.from', { id: record.splitFromShipmentId }));
    }
    if (record.splitShipmentIds && record.splitShipmentIds.length > 0) {
      lines.push(t('lotDetail.split.to', { ids: record.splitShipmentIds.join(', ') }));
    }
    if (record.lotAssignHold) {
      lines.push(t('lotDetail.split.hold'));
    }
    if (!lines.length) return null;
    return lines.map((line) => <div key={line} className="lot-detail-split-badge">{line}</div>);
  };

  const renderShipmentLink = useCallback((shipmentId) => {
    if (!shipmentId) return '-';
    const normalizedId = String(shipmentId).trim();
    if (!normalizedId) return '-';
    const basePath = shop ? `/${shop}` : '';
    const href = `${basePath}/ship/${normalizedId}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="lot-detail-shipment-link"
      >
        {normalizedId}
      </a>
    );
  }, [shop]);

  const formatMetaDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString();
  };

  const getLabel = useCallback((key, fallback, options) => {
    const value = t(key, options);
    return value === key ? fallback : value;
  }, [t]);

  const applyLotPayload = useCallback((payload, options = {}) => {
    const { personalInfoRestricted: restricted = false } = options;
    const normalized = normalizeLotPayload(payload);
    if (!normalized) return;

    setForm({
      lotNumber: normalized.lotNumber,
      destination: normalized.destination,
      status: normalized.status,
      trackingNumber: normalized.trackingNumber ?? normalized.wmsReferenceId ?? '',
      locationsOrderNumber: normalized.locationsOrderNumber ?? '',
      grossWeightKg: normalized.grossWeightKg ?? '',
      netWeightKg: normalized.netWeightKg ?? '',
      shipmentIds: normalized.shipmentIds,
    });

    setLotMeta({
      wmsStatus: normalized.wmsStatus,
      alert: normalized.alert,
      trackingNumber: normalized.trackingNumber ?? normalized.wmsReferenceId,
      entryOrderStatus: normalized.entryOrderStatus,
      createdAt: normalized.createdAt,
    });

    setLotShipmentRecords(normalizeLotShipments(normalized.shipments, { personalInfoRestricted: restricted }));
    setValidationErrors({});
    setSaveError(null);
  }, []);

  const loadLot = useCallback(async (signal) => {
    if (isNew) return;
    setLotLoading(true);
    setLotError(null);

    try {
      const response = await fetchShipLot(lotId, { signal });
      if (!response.ok) {
        const errorBody = await safeReadJson(response);
        throw new Error(errorBody?.message ?? getLabel('lotDetail.messages.loadFailed', 'ロットの取得に失敗しました'));
      }

      const payload = await response.json();
      const restricted = isPersonalInfoRestricted(payload);
      setPersonalInfoRestricted(restricted);
      setRestrictionMeta(payload?.meta ?? null);
      applyLotPayload(payload.data ?? payload, { personalInfoRestricted: restricted });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to load lot detail', error);
      setLotError(error.message ?? getLabel('lotDetail.messages.loadFailed', 'ロットの取得に失敗しました'));
    } finally {
      setLotLoading(false);
    }
  }, [applyLotPayload, getLabel, isNew, lotId]);

  const initialTemplate = useMemo(() => {
    if (lotId && LOT_TEMPLATES[lotId]) {
      return LOT_TEMPLATES[lotId];
    }
    if (isNew && LOT_TEMPLATES.new) {
      return LOT_TEMPLATES.new;
    }
    return LOT_TEMPLATES.default;
  }, [lotId, isNew]);

  const [form, setForm] = useState(() => ({
    ...initialTemplate,
    shipmentIds: [...initialTemplate.shipmentIds],
  }));

  const destinationLabel = useMemo(() => {
    const option = DESTINATION_OPTIONS.find((candidate) => candidate.value === form.destination);
    return option?.label ?? DEFAULT_COUNTRY_LABEL;
  }, [form.destination]);

  const canDownloadInvoice = useMemo(() => (
    form.status === 'shipped' && Boolean((form.trackingNumber ?? '').trim())
  ), [form.status, form.trackingNumber]);

  useEffect(() => {
    setForm({
      ...initialTemplate,
      shipmentIds: [...initialTemplate.shipmentIds],
    });
  }, [initialTemplate]);

  useEffect(() => {
    if (isNew) {
      setLotLoading(false);
      setLotMeta({
          wmsStatus: null,
          alert: 'normal',
          trackingNumber: null,
          entryOrderStatus: null,
          createdAt: null,
      });
      setLotShipmentRecords([]);
      setPersonalInfoRestricted(false);
      setRestrictionMeta(null);
      setValidationErrors({});
      setSaveError(null);
      return;
    }

    const controller = new AbortController();
    loadLot(controller.signal);
    return () => controller.abort();
  }, [isNew, loadLot]);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);

  const shipmentsById = useMemo(() => (
    shipmentDirectory.reduce((acc, record) => {
      acc[record.id] = record;
      return acc;
    }, {})
  ), [shipmentDirectory]);

  const filteredShipmentIds = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return form.shipmentIds;
    return form.shipmentIds.filter((id) => {
      const record = shipmentsById[id];
      const haystacks = [
        id,
        record?.name ?? '',
        record?.addressJa ?? '',
        record?.country ?? '',
        record?.postalCode ?? '',
        record?.status ?? '',
        record?.trackingNumber ?? '',
        record?.cnTrackingNumber ?? '',
      ].map((value) => String(value ?? '').toLowerCase());
      return haystacks.some((value) => value.includes(keyword));
    });
  }, [form.shipmentIds, searchKeyword, shipmentsById]);

  useEffect(() => {
    setPage(1);
  }, [searchKeyword, form.shipmentIds]);

  const totalPages = Math.max(1, Math.ceil(filteredShipmentIds.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedShipmentIds = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredShipmentIds.slice(start, start + PAGE_SIZE);
  }, [filteredShipmentIds, page]);

  const pageItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    return [1, 2, 3, '...', totalPages];
  }, [totalPages]);

  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [manualEntry, setManualEntry] = useState('');
  const [pendingIds, setPendingIds] = useState([]);
  const [removalIds, setRemovalIds] = useState([]);
  const [listSearch, setListSearch] = useState('');
  const [autoCount, setAutoCount] = useState(20);
  const [autoMessage, setAutoMessage] = useState('');
  const [autoStartDate, setAutoStartDate] = useState('');
  const [autoEndDate, setAutoEndDate] = useState('');

  /* ── WMSログ state ─────────────────────────────────── */
  const [wmsLogs, setWmsLogs] = useState([]);
  const [wmsLogsLoading, setWmsLogsLoading] = useState(false);
  const [wmsLogsError, setWmsLogsError] = useState(null);
  const [wmsLogsPage, setWmsLogsPage] = useState(1);
  const [wmsLogsTotalPages, setWmsLogsTotalPages] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState(null);

  const loadWmsLogs = useCallback(async (pageNum = 1, signal) => {
    if (isNew || !lotId) return;
    setWmsLogsLoading(true);
    setWmsLogsError(null);
    try {
      const response = await fetchLotWmsLogs(lotId, { page: pageNum }, { signal });
      if (!response.ok) throw new Error(t('wmsLog.fetchError'));
      const payload = await response.json();
      setWmsLogs(payload.data ?? []);
      setWmsLogsTotalPages(payload.last_page ?? 1);
      setWmsLogsPage(payload.current_page ?? pageNum);
    } catch (error) {
      if (error.name === 'AbortError') return;
      setWmsLogsError(error.message ?? t('wmsLog.fetchError'));
    } finally {
      setWmsLogsLoading(false);
    }
  }, [isNew, lotId, t]);

  useEffect(() => {
    if (isNew || !lotId) return;
    const controller = new AbortController();
    loadWmsLogs(1, controller.signal);
    return () => controller.abort();
  }, [isNew, lotId, loadWmsLogs]);

  const handleWmsLogsPageChange = useCallback((newPage) => {
    setExpandedLogId(null);
    loadWmsLogs(newPage);
  }, [loadWmsLogs]);

  const formatLogDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  const fetchShipmentDirectory = useCallback(async (signal) => {
    setShipmentsLoading(true);
    setShipmentsError(null);

    try {
      const params = buildShipmentQuery();
      const response = await fetchShipments(params, { signal });

      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }

      const payload = await response.json();
      const restricted = isPersonalInfoRestricted(payload);
      setPersonalInfoRestricted(restricted);
      setRestrictionMeta(payload?.meta ?? null);
      const data = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      const normalized = data
        .map((shipment) => {
          const id = shipment?.id ?? shipment?.shipment_id;
          if (!id) return null;

          const rawCount = Number(
            shipment?.card_count
            ?? shipment?.cards_count
            ?? shipment?.cardCount
            ?? shipment?.card_counts
          );
          const cardCount = Number.isFinite(rawCount) ? rawCount : null;

          const primaryAddress = shipment?.client_address ?? shipment?.client_address_en;
          const fallbackCountry = shipment?.country
            ?? shipment?.client_country
            ?? shipment?.destination_country
            ?? shipment?.country_name;
          const postalCode = extractPostalCode(shipment?.client_address)
            ?? cleanTextValue(shipment?.postal_code);
          const status = parseShipmentStatusCode(
            shipment?.status
            ?? shipment?.status_code
            ?? shipment?.statusId
            ?? shipment?.status_id
            ?? shipment?.shipment_status,
          );

          return {
            id: String(id),
            name: shipment?.client_name ?? '-',
            addressJa: formatAddress(shipment?.client_address),
            postalCode: postalCode ?? null,
            country: formatCountry(primaryAddress, fallbackCountry, DEFAULT_COUNTRY_LABEL),
            cardCount,
            status,
            splitFromShipmentId: shipment?.split_from_shipment_id ?? null,
            splitShipmentIds: shipment?.split_shipment_ids ?? [],
            lotAssignHold: Boolean(shipment?.lot_assign_hold),
          };
        })
        .filter(Boolean);

      setShipmentDirectorySource(normalized.length ? normalized : []);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to load shipment directory', error);
      setShipmentsError(t('lot.fetchError'));
      setShipmentDirectorySource((prev) => (prev.length ? prev : []));
    } finally {
      setShipmentsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchShipmentDirectory(controller.signal);
    return () => controller.abort();
  }, [fetchShipmentDirectory]);

  const directoryCandidates = useMemo(() => {
    const keyword = listSearch.trim().toLowerCase();
    if (!keyword) return shipmentDirectory;
    return shipmentDirectory.filter((record) => (
      [record.id, record.name, record.addressJa, record.country]
        .some((value) => String(value).toLowerCase().includes(keyword))
    ));
  }, [listSearch, shipmentDirectory]);

  const rangeIds = useMemo(() => {
    const start = parseInt(rangeStart, 10);
    const end = parseInt(rangeEnd, 10);
    if (Number.isNaN(start) || Number.isNaN(end)) return [];
    const low = Math.min(start, end);
    const high = Math.max(start, end);
    const result = [];
    for (let current = low; current <= high; current += 1) {
      if (result.length >= 200) break;
      result.push(String(current));
    }
    return result;
  }, [rangeStart, rangeEnd]);

  const rangeCount = rangeIds.length;
  const rangeSummary = getLabel(
    'lotDetail.modal.range.count',
    rangeCount ? `${rangeCount} IDs ready` : 'No IDs in range',
    { count: rangeCount },
  );
  const autoStartLabel = getLabel('lotDetail.modal.auto.startLabel', '対象期間の開始日');
  const autoEndLabel = getLabel('lotDetail.modal.auto.endLabel', '対象期間の終了日');
  const autoUnsetLabel = getLabel('lotDetail.modal.auto.unset', '未選択');
  const getAutoDateLabel = (value) => (value ? value : autoUnsetLabel);

  const addPendingIds = (ids) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (!form.shipmentIds.includes(id)) {
          next.add(id);
        }
      });
      return Array.from(next);
    });
  };

  const openAddModal = () => {
    setRangeStart('');
    setRangeEnd('');
    setManualEntry('');
    setPendingIds([]);
    setListSearch('');
    setRemovalIds([]);
    setAutoCount(20);
    setAutoMessage('');
    setAutoStartDate('');
    setAutoEndDate('');
    setAddModalOpen(true);
  };

  const closeAddModal = () => setAddModalOpen(false);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRemoveShipment = (shipmentId) => {
    if (!window.confirm(t('lotDetail.messages.removeShipment', { shipmentId }))) return;
    setForm((prev) => ({
      ...prev,
      shipmentIds: prev.shipmentIds.filter((id) => id !== shipmentId),
    }));
  };

  const handleAddShipment = () => openAddModal();

  const handleSave = async () => {
    const successMessage = getLabel('lotDetail.messages.saveSuccess', '保存しました');
    const failureMessage = getLabel('lotDetail.messages.saveFailed', '保存に失敗しました');

    const payload = {
      destination_code: form.destination,
      destination_label: DESTINATION_OPTIONS.find((option) => option.value === form.destination)?.label,
      status: form.status,
      tracking_number: (form.trackingNumber ?? '').trim(),
      locations_order_number: (form.locationsOrderNumber ?? '').trim(),
      shipment_ids: form.shipmentIds
        .map((id) => Number(id))
        .filter((value) => Number.isFinite(value)),
    };

    // 箱单用の重量 (kg)。空欄は「未入力」なので送らない (バックエンドは nullable numeric)
    if (String(form.grossWeightKg ?? '').trim() !== '') {
      payload.gross_weight_kg = Number(form.grossWeightKg);
    }
    if (String(form.netWeightKg ?? '').trim() !== '') {
      payload.net_weight_kg = Number(form.netWeightKg);
    }

    setSaving(true);
    setSaveError(null);
    setValidationErrors({});

    try {
      const response = isNew
        ? await createShipLot(payload)
        : await updateShipLot(form.lotNumber || lotId, payload);

      if (!response.ok) {
        const errorBody = await safeReadJson(response);
        if (errorBody?.errors) {
          setValidationErrors(errorBody.errors);
        }
        throw new Error(errorBody?.message ?? failureMessage);
      }

      const body = await response.json();
      const lotPayload = body.data ?? body;
      const restricted = isPersonalInfoRestricted(body);
      setPersonalInfoRestricted(restricted);
      setRestrictionMeta(body?.meta ?? null);
      applyLotPayload(lotPayload, { personalInfoRestricted: restricted });

      if (isNew && lotPayload?.lotId) {
        navigate(`/ship/lots/${lotPayload.lotId}`, { replace: true });
      }

      setValidationErrors({});
      setSaveError(null);
      alert(successMessage);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to save lot', error);
      const message = error.message ?? failureMessage;
      setSaveError(message);
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadLocations = async () => {
    if (isNew) return;
    try {
      const response = await downloadLotLocations(form.lotNumber || lotId);
      if (!response.ok) {
        alert(t('common.error'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dt = new Date();
      link.setAttribute('download', `locations_${form.lotNumber || lotId}_${dt.getFullYear()}${dt.getMonth() + 1}${dt.getDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      alert(t('common.error'));
    }
  };

  const handleDownloadInvoice = async () => {
    if (isNew) return;
    try {
      const response = await downloadLotInvoice(form.lotNumber || lotId);
      if (!response.ok) {
        alert(t('common.error'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dt = new Date();
      link.setAttribute('download', `invoice_${form.lotNumber || lotId}_${dt.getFullYear()}${dt.getMonth() + 1}${dt.getDate()}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      alert(t('common.error'));
    }
  };

  const handleDownloadPackingList = async () => {
    if (isNew) return;
    try {
      const response = await downloadLotPackingList(form.lotNumber || lotId);
      if (!response.ok) {
        const errorBody = await safeReadJson(response);
        if (errorBody?.errors) {
          setValidationErrors(errorBody.errors);
        }
        alert(errorBody?.message ?? t('common.error'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dt = new Date();
      link.setAttribute('download', `packing_list_${form.lotNumber || lotId}_${dt.getFullYear()}${dt.getMonth() + 1}${dt.getDate()}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      alert(t('common.error'));
    }
  };

  const handleDownloadPurchaseDetail = async () => {
    if (isNew) return;
    try {
      const response = await downloadLotPurchaseDetail(form.lotNumber || lotId);
      if (!response.ok) {
        const errorBody = await safeReadJson(response);
        if (errorBody?.errors) {
          setValidationErrors(errorBody.errors);
        }
        alert(errorBody?.message ?? t('common.error'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dt = new Date();
      link.setAttribute('download', `purchase_detail_${form.lotNumber || lotId}_${dt.getFullYear()}${dt.getMonth() + 1}${dt.getDate()}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      alert(t('common.error'));
    }
  };

  const handleResyncEntry = async () => {
    if (isNew || resyncingEntry) return;
    setResyncingEntry(true);
    try {
      const response = await resyncEntryOrder(form.lotNumber || lotId);
      if (response.ok) {
        const data = await response.json();
        setLotMeta((prev) => ({
          ...prev,
          entryOrderStatus: data.entry_order_status ?? prev.entryOrderStatus,
        }));
      }
    } catch (error) {
      console.error('Failed to resync entry order', error);
    } finally {
      setResyncingEntry(false);
    }
  };

  const handleSyncDelivery = async (force = false) => {
    if (isNew || syncingDelivery) return;
    setSyncingDelivery(true);
    try {
      const response = await syncDeliveryOrder(form.lotNumber || lotId, { force });
      const data = await response.json().catch(() => null);

      if (response.status === 409 && data?.status === 'confirmation_required') {
        const confirmed = window.confirm(t('lotDetail.actions.syncDeliveryForceConfirm'));
        if (confirmed) {
          setSyncingDelivery(false);
          return handleSyncDelivery(true);
        }
        return;
      }

      if (response.ok) {
        setLotMeta((prev) => ({
          ...prev,
          wmsStatus: data?.wms_status ?? prev.wmsStatus,
          wmsReferenceId: data?.wms_reference_id ?? prev.wmsReferenceId,
        }));
      } else {
        const msg = data?.message || data?.errors?.lot?.[0] || t('lotDetail.actions.syncDeliveryFailed');
        alert(msg);
      }
    } catch (error) {
      console.error('Failed to sync delivery order', error);
    } finally {
      setSyncingDelivery(false);
    }
  };

  const handleCancelDelivery = async () => {
    if (isNew || cancelingDelivery) return;
    const confirmed = window.confirm(t('lotDetail.actions.cancelDeliveryConfirm'));
    if (!confirmed) return;

    setCancelingDelivery(true);
    try {
      const response = await cancelDeliveryOrder(form.lotNumber || lotId);
      const data = await response.json().catch(() => null);

      if (response.ok) {
        // キャンセル成功で wms_status は null(未送信)に戻る。?? は null を握り潰すため明示的に反映する。
        setLotMeta((prev) => ({
          ...prev,
          wmsStatus: data && 'wms_status' in data ? data.wms_status : prev.wmsStatus,
        }));
      } else {
        const msg = data?.message || data?.errors?.lot?.[0] || t('lotDetail.actions.cancelDeliveryFailed');
        alert(msg);
      }
    } catch (error) {
      console.error('Failed to cancel delivery order', error);
    } finally {
      setCancelingDelivery(false);
    }
  };

  const handleDeleteLot = async () => {
    if (isNew) return;
    const identifier = form.lotNumber || lotId;
    if (!identifier) return;

    const confirmMessage = getLabel('lotDetail.messages.deleteConfirm', 'このロットを削除しますか？', { lotId: identifier });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const successMessage = getLabel('lotDetail.messages.deleteSuccess', 'ロットを削除しました');
    const failureMessage = getLabel('lotDetail.messages.deleteFailed', 'ロットの削除に失敗しました');

    setDeleting(true);
    try {
      const response = await deleteShipLot(identifier);
      if (!response.ok) {
        const errorBody = await safeReadJson(response);
        throw new Error(errorBody?.message ?? failureMessage);
      }

      alert(successMessage);
      navigate('/ship/lots');
    } catch (error) {
      console.error('Failed to delete lot', error);
      alert(error.message ?? failureMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleBack = () => navigate(-1);

  const handleRangeAdd = () => {
    if (!rangeCount) return;
    addPendingIds(rangeIds);
  };

  const handleManualAdd = () => {
    const trimmed = manualEntry.trim();
    if (!trimmed) return;
    addPendingIds([trimmed]);
    setManualEntry('');
  };

  const handleRemoveStaged = (id) => {
    setPendingIds((prev) => prev.filter((item) => item !== id));
  };

  const handleDirectoryToggle = (id, inLot) => {
    if (inLot) {
      setRemovalIds((prev) => (
        prev.includes(id)
          ? prev.filter((item) => item !== id)
          : [...prev, id]
      ));
      setPendingIds((prev) => prev.filter((item) => item !== id));
      return;
    }
    setPendingIds((prev) => (
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    ));
  };

  const handleAutoGenerate = () => {
    const available = shipmentDirectory
      .map((entry) => entry.id)
      .filter((id) => !form.shipmentIds.includes(id));
    const count = Math.max(0, Math.min(Number(autoCount) || 0, available.length));
    if (!count) {
      setAutoMessage(t('lotDetail.modal.auto.empty'));
      return;
    }
    addPendingIds(available.slice(0, count));
    setAutoMessage(
      t('lotDetail.modal.auto.message', {
        count,
        start: getAutoDateLabel(autoStartDate),
        end: getAutoDateLabel(autoEndDate),
      }),
    );
  };

  const normalizeDateValue = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return value;
  };

  const handleAutoDateChange = (field, value) => {
    const normalized = normalizeDateValue(value);
    if (field === 'start') {
      setAutoStartDate(normalized);
      if (autoEndDate && normalized && autoEndDate < normalized) {
        setAutoEndDate(normalized);
      }
      return;
    }
    setAutoEndDate(normalized);
    if (autoStartDate && normalized && normalized < autoStartDate) {
      setAutoStartDate(normalized);
    }
  };

  const handleModalApply = () => {
    setForm((prev) => {
      const remaining = prev.shipmentIds.filter((id) => !removalIds.includes(id));
      const merged = Array.from(new Set([...remaining, ...pendingIds]));
      return {
        ...prev,
        shipmentIds: merged,
      };
    });
    closeAddModal();
  };

  const modalHasChanges = pendingIds.length || removalIds.length;

  const modalSelections = useMemo(() => {
    const entries = form.shipmentIds.map((id) => ({
      id,
      label: removalIds.includes(id) ? 'remove' : 'existing',
    }));

    const existingSet = new Set(form.shipmentIds);
    pendingIds.forEach((id) => {
      if (!existingSet.has(id)) {
        entries.push({ id, label: 'add' });
      }
    });

    return entries;
  }, [form.shipmentIds, pendingIds, removalIds]);

  const pendingSummary = t('lotDetail.modal.list.selection', { count: pendingIds.length });
  const removalSummary = t('lotDetail.modal.list.unselect', { count: removalIds.length });

  const headingTitle = isNew ? t('lotDetail.titleNew') : t('lotDetail.titleDetail');

  return (
    <>
      <Header />
      <SideMenu />
      <div className="lot-detail-page">
        <div className="lot-detail-toolbar">
          <div className="lot-detail-heading">
            <p className="lot-detail-title">
              {headingTitle}
            </p>
            <p className="lot-detail-subtitle">{t('lotDetail.subtitle')}</p>
            <div className="lot-detail-heading-meta">
              <div className="lot-detail-heading-item">
                <span className="lot-detail-heading-label">{t('lotDetail.labels.lotNumber')}</span>
                <span className="lot-detail-heading-value">{form.lotNumber || t('lotDetail.newLot')}</span>
              </div>
              <div className="lot-detail-heading-item">
                <span className="lot-detail-heading-label">{t('lotDetail.labels.destination')}</span>
                <span className="lot-detail-heading-value">{destinationLabel}</span>
              </div>
              {lotMeta.createdAt && (
                <div className="lot-detail-heading-item">
                  <span className="lot-detail-heading-label">{t('lot.table.createdAt')}</span>
                  <span className="lot-detail-heading-value">{formatMetaDate(lotMeta.createdAt)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="lot-detail-toolbar-actions">
            <button type="button" className="lot-detail-btn outline lot-detail-help-btn" onClick={() => setShowHelp(true)}>
              ?
            </button>
            <button type="button" className="lot-detail-btn outline" onClick={handleBack}>
              {t('lotDetail.actions.back')}
            </button>
            {!isNew && (
              <button
                type="button"
                className="lot-detail-btn danger"
                onClick={handleDeleteLot}
                disabled={saving || lotLoading || deleting}
              >
                {getLabel('lotDetail.actions.delete', '削除')}
              </button>
            )}
            <button
              type="button"
              className="lot-detail-btn primary"
              onClick={handleSave}
              disabled={saving || lotLoading}
            >
              {t('lotDetail.actions.save')}
            </button>
          </div>
        </div>

        {personalInfoRestricted && (
          <div className="lot-detail-restriction-hint">
            <p>{t('lotDetail.messages.personalInfoRestricted')}</p>
            {restrictionMeta?.message && (
              <p>{restrictionMeta.message}</p>
            )}
            {restrictionMeta?.restriction_reason && (
              <p>{t('lotDetail.messages.personalInfoRestrictedReason', { reason: restrictionMeta.restriction_reason })}</p>
            )}
          </div>
        )}

        {/* {(lotError || saveError || validationSummary.length) && (
          <div className="lot-detail-error">
            {lotError && <p>{lotError}</p>}
            {saveError && <p>{saveError}</p>}
            {validationSummary.length > 0 && (
              <ul className="lot-detail-validation-list">
                {validationSummary.map((message, index) => (
                  <li key={`validation-${index}`}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )} */}
        {lotLoading && !isNew && (
          <p className="lot-detail-loading">{t('lot.loading')}</p>
        )}

        <section className="lot-detail-card">
          <h2 className="lot-detail-section-title">{t('lotDetail.basicInfo')}</h2>

          {/* Row 1: ステータスカード横並び */}
          <div className="lot-detail-status-row">
            {/* ロットステータス */}
            <div className="lot-detail-status-card">
              <label>{t('lotDetail.labels.status')}</label>
              <select
                className="lot-detail-status"
                value={form.status}
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </select>
              {renderFieldError('status')}
            </div>

            {/* WMS連携ステータス（読み取り専用） */}
            {!isNew && (
              <div className="lot-detail-status-card lot-detail-status-card--wide">
                <label>{getLabel('lotDetail.labels.wmsLink', 'WMS連携')}</label>
                <div className="lot-detail-wms-status-group">
                  <div className="lot-detail-wms-status-item">
                    <span className="lot-detail-wms-status-label">{t('lot.wmsLabels.entry')}</span>
                    <span className={`lot-detail-entry-badge ${
                      lotMeta.entryOrderStatus === 7 ? 'completed'
                        : lotMeta.entryOrderStatus === 1 ? 'sent'
                        : 'unsent'
                    }`}>
                      {lotMeta.entryOrderStatus === 7
                        ? t('lotDetail.entryStatus.completed')
                        : lotMeta.entryOrderStatus === 1
                          ? t('lotDetail.entryStatus.sent')
                          : t('lotDetail.entryStatus.unsent')}
                    </span>
                  </div>
                  <div className="lot-detail-wms-status-item">
                    <span className="lot-detail-wms-status-label">{t('lot.wmsLabels.delivery')}</span>
                    <span className={`lot-detail-entry-badge ${
                      lotMeta.wmsStatus === 7 || lotMeta.wmsStatus === 3 ? 'completed'
                        : lotMeta.wmsStatus === 1 ? 'sent'
                        : 'unsent'
                    }`}>
                      {lotMeta.wmsStatus === 7 || lotMeta.wmsStatus === 3
                        ? t('lot.wmsStatus.confirmed')
                        : lotMeta.wmsStatus === 1
                          ? t('lot.wmsStatus.sent')
                          : t('lot.wmsStatus.unsent')}
                    </span>
                  </div>
                </div>
                {(lotMeta.entryOrderStatus === null || lotMeta.entryOrderStatus === 1) && (
                  <button
                    type="button"
                    className="lot-detail-btn outline lot-detail-resync-btn"
                    onClick={handleResyncEntry}
                    disabled={resyncingEntry || saving}
                  >
                    {resyncingEntry
                      ? t('lotDetail.actions.resyncing')
                      : t('lotDetail.actions.resyncEntry')}
                  </button>
                )}
                {(lotMeta.wmsStatus === null || lotMeta.wmsStatus === undefined) && (
                  <button
                    type="button"
                    className="lot-detail-btn outline lot-detail-resync-btn"
                    onClick={() => handleSyncDelivery()}
                    disabled={syncingDelivery || saving}
                  >
                    {syncingDelivery
                      ? t('lotDetail.actions.resyncing')
                      : lotMeta.entryOrderStatus !== 7
                        ? t('lotDetail.actions.syncDeliveryForce')
                        : t('lotDetail.actions.syncDelivery')}
                  </button>
                )}
                {lotMeta.wmsStatus === 1 && (
                  <button
                    type="button"
                    className="lot-detail-btn outline lot-detail-resync-btn"
                    onClick={handleCancelDelivery}
                    disabled={cancelingDelivery || saving}
                  >
                    {cancelingDelivery
                      ? t('lotDetail.actions.resyncing')
                      : t('lotDetail.actions.cancelDelivery')}
                  </button>
                )}
                <button
                  type="button"
                  className="lot-detail-btn outline"
                  onClick={handleDownloadLocations}
                  disabled={saving || !form.shipmentIds.length}
                >
                  {t('lotDetail.actions.downloadLocations')}
                </button>
                {canDownloadInvoice && (
                  <button
                    type="button"
                    className="lot-detail-btn outline"
                    onClick={handleDownloadInvoice}
                    disabled={saving}
                  >
                    {getLabel('lotDetail.actions.downloadInvoice', 'インボイスDL')}
                  </button>
                )}
                {canDownloadInvoice && (
                  <button
                    type="button"
                    className="lot-detail-btn outline"
                    onClick={handleDownloadPackingList}
                    disabled={saving}
                  >
                    {getLabel('lotDetail.actions.downloadPackingList', '箱单DL')}
                  </button>
                )}
                {canDownloadInvoice && (
                  <button
                    type="button"
                    className="lot-detail-btn outline"
                    onClick={handleDownloadPurchaseDetail}
                    disabled={saving}
                  >
                    {getLabel('lotDetail.actions.downloadPurchaseDetail', '明細表DL')}
                  </button>
                )}
              </div>
            )}

            {/* アラート（読み取り専用） */}
            <div className="lot-detail-status-card">
              <label>{t('lot.filters.alert')}</label>
              {renderAlertBadge(lotMeta.alert) || '-'}
            </div>
          </div>

          {/* Row 2: 追跡番号・ロケーションズ注文番号（横並び） */}
          <div className="lot-detail-tracking-row">
            <div className="lot-detail-field">
              <label>{getLabel('lotDetail.labels.trackingNumber', '追跡番号')}</label>
              <input
                type="text"
                value={form.trackingNumber ?? ''}
                placeholder={getLabel('lotDetail.placeholders.trackingNumber', '追跡番号を入力')}
                onChange={(e) => handleFieldChange('trackingNumber', e.target.value)}
              />
              {renderFieldError('tracking_number')}
            </div>
            <div className="lot-detail-field">
              <label>{getLabel('lotDetail.labels.locationsOrderNumber', 'ロケーションズ注文番号')}</label>
              <input
                type="text"
                value={form.locationsOrderNumber ?? ''}
                placeholder="china_0001"
                onChange={(e) => handleFieldChange('locationsOrderNumber', e.target.value)}
              />
              {renderFieldError('locations_order_number')}
            </div>
          </div>

          {/* Row 3: 箱单用の重量（横並び） */}
          <div className="lot-detail-tracking-row">
            <div className="lot-detail-field">
              <label>{getLabel('lotDetail.labels.grossWeightKg', '荷物重量 Gross (kg)')}</label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.grossWeightKg ?? ''}
                placeholder="0.7"
                onChange={(e) => handleFieldChange('grossWeightKg', e.target.value)}
              />
              {renderFieldError('gross_weight_kg')}
            </div>
            <div className="lot-detail-field">
              <label>{getLabel('lotDetail.labels.netWeightKg', 'カード重量 Net (kg・空欄で自動計算)')}</label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.netWeightKg ?? ''}
                placeholder={getLabel('lotDetail.placeholders.netWeightKg', '空欄: Gross−封筒30g×発送数')}
                onChange={(e) => handleFieldChange('netWeightKg', e.target.value)}
              />
              {renderFieldError('net_weight_kg')}
            </div>
          </div>

          <p className="lot-detail-hint" style={{ color: '#6b7280', fontSize: '0.82rem', margin: '0.25rem 0 0.75rem', lineHeight: '1.5' }}>
            {getLabel('lotDetail.hints.wmsAutoUpdate', 'WMSから出荷確認を受信すると、追跡番号・物流コード・重量が自動更新されます。ステータスも自動的に「発送済み」に変更されます。')}
          </p>

          <div className="lot-detail-field lot-detail-field--full">
            <label>{t('lotDetail.labels.shipmentIds')}</label>
            <div className="lot-detail-chip-row">
              <div className="lot-detail-chip-list">
                {form.shipmentIds.map((id) => (
                  <span key={id} className="lot-detail-chip">
                    {renderShipmentLink(id)}
                    <button type="button" aria-label={t('lotDetail.actions.remove')} onClick={() => handleRemoveShipment(id)}>
                      ×
                    </button>
                  </span>
                ))}
                {!form.shipmentIds.length && (
                  <span className="lot-detail-chip-placeholder">{t('lotDetail.labels.noShipments')}</span>
                )}
              </div>
              <button type="button" className="lot-detail-chip-add" onClick={handleAddShipment}>
                <span>＋</span> {t('lotDetail.actions.addShipment')}
              </button>
            </div>
            {renderFieldError('shipment_ids')}
          </div>
        </section>

        <section className="lot-detail-card">
          <div className="lot-detail-table-toolbar">
            <label>{t('lotDetail.labels.searchShipment')}</label>
            <div className="lot-detail-search">
              <span className="lot-detail-search-icon" aria-hidden="true"></span>
              <input
                type="text"
                value={searchKeyword}
                placeholder={t('lotDetail.placeholders.searchShipment')}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
            </div>
          </div>
          <div className="lot-detail-table-wrapper">
            <table className="lot-detail-table">
              <thead>
                <tr>
                  <th>{t('lotDetail.table.shipmentId')}</th>
                  <th>{t('lotDetail.table.name')}</th>
                  <th>{t('lotDetail.table.cardCount')}</th>
                  <th>{t('lotDetail.table.trackingNumber')}</th>
                  <th>{t('lotDetail.table.cnTrackingNumber')}</th>
                  <th>{t('lotDetail.table.country')}</th>
                  <th>{t('lotDetail.table.postalCode')}</th>
                  <th>{t('lotDetail.table.addressJa')}</th>
                  <th>{t('lotDetail.table.status')}</th>
                  <th>{t('lotDetail.table.action')}</th>
                </tr>
              </thead>
              <tbody>
                {!pagedShipmentIds.length && (
                  <tr>
                    <td colSpan="10" className="lot-detail-empty">{t('lotDetail.table.empty')}</td>
                  </tr>
                )}
                {pagedShipmentIds.map((id) => {
                  const record = shipmentsById[id];
                  return (
                    <tr key={id}>
                      <td>{renderShipmentLink(id)}{renderSplitBadge(record)}</td>
                      <td>{record?.name ?? '-'}</td>
                      <td>{typeof record?.cardCount === 'number' ? record.cardCount.toLocaleString() : '-'}</td>
                      <td>{record?.trackingNumber ?? '-'}</td>
                      <td>{record?.cnTrackingNumber ?? '-'}</td>
                      <td>{personalInfoRestricted ? '***' : (record?.country ?? '-')}</td>
                      <td>{personalInfoRestricted ? '***' : (record?.postalCode ?? '-')}</td>
                      <td>{personalInfoRestricted ? '***' : (record?.addressJa ?? '-')}</td>
                      <td className="lot-detail-status-cell">{renderShipmentStatus(record?.status)}</td>
                      <td>
                        <button type="button" className="lot-detail-icon-btn" onClick={() => handleRemoveShipment(id)}>
                          {t('lotDetail.actions.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="lot-detail-footer">
            <div className="lot-detail-pagination">
              <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page === 1}>
                {t('lotDetail.pagination.prev')}
              </button>
              <div className="lot-detail-pages">
                {pageItems.map((item, index) => (
                  <button
                    key={`${item}-${index}`}
                    type="button"
                    className={item === page ? 'active' : ''}
                    disabled={item === '...'}
                    onClick={() => item !== '...' && setPage(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
                {t('lotDetail.pagination.next')}
              </button>
            </div>
          </div>
        </section>

        {/* ── WMSログセクション ────────────────────────── */}
        {!isNew && (
          <section className="lot-detail-card">
            <h2 className="lot-detail-wms-title">{t('wmsLog.title')}</h2>

            {wmsLogsLoading && <p className="lot-detail-wms-loading">{t('wmsLog.loading')}</p>}
            {wmsLogsError && <p className="lot-detail-wms-error">{wmsLogsError}</p>}

            {!wmsLogsLoading && !wmsLogsError && (
              <>
                <div className="lot-detail-table-wrapper">
                  <table className="lot-detail-table lot-detail-wms-table">
                    <thead>
                      <tr>
                        <th>{t('wmsLog.table.datetime')}</th>
                        <th>{t('wmsLog.table.method')}</th>
                        <th>{t('wmsLog.table.direction')}</th>
                        <th>{t('wmsLog.table.result')}</th>
                        <th>{t('wmsLog.table.errorMessage')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!wmsLogs.length && (
                        <tr>
                          <td colSpan="5" className="lot-detail-empty">{t('wmsLog.empty')}</td>
                        </tr>
                      )}
                      {wmsLogs.map((log) => (
                        <React.Fragment key={log.id}>
                          <tr
                            className={`lot-detail-wms-row ${expandedLogId === log.id ? 'expanded' : ''}`}
                            onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                          >
                            <td>{formatLogDate(log.created_at)}</td>
                            <td>{log.method ?? '-'}</td>
                            <td>
                              <span className={`lot-detail-wms-badge ${log.direction === 'outbound' ? 'outbound' : 'inbound'}`}>
                                {log.direction === 'outbound' ? t('wmsLog.filters.outbound') : t('wmsLog.filters.inbound')}
                              </span>
                            </td>
                            <td>
                              <span className={`lot-detail-wms-badge ${log.success ? 'success' : 'failure'}`}>
                                {log.success ? t('wmsLog.filters.success') : t('wmsLog.filters.failure')}
                              </span>
                            </td>
                            <td>{log.error_message || '-'}</td>
                          </tr>
                          {expandedLogId === log.id && (
                            <tr className="lot-detail-wms-expand-row">
                              <td colSpan="5">
                                <div className="lot-detail-wms-payload">
                                  <div>
                                    <strong>{t('wmsLog.detail.request')}</strong>
                                    <pre>{log.request_payload ? (() => { try { return JSON.stringify(JSON.parse(log.request_payload), null, 2); } catch { return log.request_payload; } })() : t('wmsLog.detail.noData')}</pre>
                                  </div>
                                  <div>
                                    <strong>{t('wmsLog.detail.response')}</strong>
                                    <pre>{log.response_payload ? (() => { try { return JSON.stringify(JSON.parse(log.response_payload), null, 2); } catch { return log.response_payload; } })() : t('wmsLog.detail.noData')}</pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {wmsLogsTotalPages > 1 && (
                  <div className="lot-detail-footer">
                    <div className="lot-detail-pagination">
                      <button type="button" onClick={() => handleWmsLogsPageChange(wmsLogsPage - 1)} disabled={wmsLogsPage <= 1}>
                        {t('wmsLog.pagination.prev')}
                      </button>
                      <span className="lot-detail-wms-page-info">{wmsLogsPage} / {wmsLogsTotalPages}</span>
                      <button type="button" onClick={() => handleWmsLogsPageChange(wmsLogsPage + 1)} disabled={wmsLogsPage >= wmsLogsTotalPages}>
                        {t('wmsLog.pagination.next')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>

      {isAddModalOpen && (
        <div className="lot-detail-modal-overlay">
          <div className="lot-detail-modal" role="dialog" aria-modal="true">
            <div className="lot-detail-modal-header">
              <div>
                <p className="lot-detail-modal-title">{t('lotDetail.modal.title')}</p>
              </div>
              <button type="button" className="lot-detail-modal-close" onClick={closeAddModal} aria-label={t('lotDetail.actions.back')}>
                ×
              </button>
            </div>

            <div className="lot-detail-modal-body">
              <div className="lot-detail-modal-column">
                <div className="lot-detail-modal-block">
                  <p className="lot-detail-modal-block-title">{t('lotDetail.modal.range.title')}</p>
                  <div className="lot-detail-modal-range">
                    <label>
                      {getLabel('lotDetail.modal.range.start', 'Start ID')}
                      <input
                        type="number"
                        value={rangeStart}
                        onChange={(e) => setRangeStart(e.target.value)}
                      />
                    </label>
                    <span className="lot-detail-modal-range-sep">~</span>
                    <label>
                      {getLabel('lotDetail.modal.range.end', 'End ID')}
                      <input
                        type="number"
                        value={rangeEnd}
                        onChange={(e) => setRangeEnd(e.target.value)}
                      />
                    </label>
                    <button type="button" onClick={handleRangeAdd} disabled={!rangeCount}>
                      {getLabel('lotDetail.modal.range.add', 'Add range')}
                    </button>
                  </div>
                  <p className="lot-detail-modal-hint subtle">{rangeSummary}</p>
                </div>

                <div className="lot-detail-modal-block">
                  <p className="lot-detail-modal-block-title">{t('lotDetail.modal.auto.title')}</p>
                  <div className="lot-detail-modal-auto-grid">
                    <label>
                      {t('lotDetail.modal.auto.countLabel')}
                      <input
                        type="number"
                        min="1"
                        value={autoCount}
                        onChange={(e) => setAutoCount(e.target.value)}
                      />
                    </label>
                    <label>
                      {autoStartLabel}
                      <input
                        type="date"
                        value={autoStartDate}
                        onChange={(e) => handleAutoDateChange('start', e.target.value)}
                      />
                    </label>
                    <label>
                      {autoEndLabel}
                      <input
                        type="date"
                        value={autoEndDate}
                        onChange={(e) => handleAutoDateChange('end', e.target.value)}
                      />
                    </label>
                    <button type="button" className="lot-detail-modal-primary" onClick={handleAutoGenerate}>
                      {t('lotDetail.modal.auto.generate')}
                    </button>
                  </div>
                  {autoMessage && <p className="lot-detail-modal-hint">{autoMessage}</p>}
                </div>

                <div className="lot-detail-modal-block">
                  <p className="lot-detail-modal-block-title">{t('lotDetail.modal.manual.title')}</p>
                  <div className="lot-detail-modal-manual">
                    <input
                      type="text"
                      value={manualEntry}
                      placeholder={t('lotDetail.modal.manual.placeholder')}
                      onChange={(e) => setManualEntry(e.target.value)}
                    />
                    <button type="button" onClick={handleManualAdd}>{t('lotDetail.modal.manual.add')}</button>
                  </div>
                </div>

                <div className="lot-detail-modal-block">
                  <p className="lot-detail-modal-block-title">{t('lotDetail.modal.manual.currentSelection')}</p>
                  <div className="lot-detail-chip-list">
                    {modalSelections.length === 0 && (
                      <span className="lot-detail-chip-placeholder">{t('lotDetail.modal.manual.empty')}</span>
                    )}
                    {modalSelections.map(({ id, label }) => (
                      <span key={id} className={`lot-detail-chip ${label}`}>
                        <span>{id}</span>
                        {label === 'add' && (
                          <button type="button" onClick={() => handleRemoveStaged(id)} aria-label={t('lotDetail.actions.remove')}>
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lot-detail-modal-column">
                <label className="lot-detail-modal-search">
                  <input
                    type="text"
                    value={listSearch}
                    placeholder={t('lotDetail.modal.list.search')}
                    onChange={(e) => setListSearch(e.target.value)}
                  />
                </label>
                {shipmentsLoading && (
                  <p className="lot-detail-modal-hint subtle">{t('lot.loading')}</p>
                )}
                {shipmentsError && (
                  <p className="lot-detail-modal-hint">{shipmentsError}</p>
                )}
                <div className="lot-detail-modal-table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('lotDetail.modal.list.id')}</th>
                        <th>{t('lotDetail.modal.list.name')}</th>
                        <th>{t('lotDetail.modal.list.cardCount')}</th>
                        <th>{t('lotDetail.table.country')}</th>
                        <th>{t('lotDetail.table.postalCode')}</th>
                        <th>{t('lotDetail.table.addressJa')}</th>
                        <th>{t('lotDetail.table.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!directoryCandidates.length && (
                        <tr>
                          <td colSpan="7" className="lot-detail-modal-empty">{t('lotDetail.modal.list.empty')}</td>
                        </tr>
                      )}
                      {directoryCandidates.map((record) => {
                        const inLot = form.shipmentIds.includes(record.id);
                        const isRemoved = removalIds.includes(record.id);
                        const isPending = pendingIds.includes(record.id);
                        const checked = inLot ? !isRemoved : isPending;
                        return (
                          <tr key={record.id}>
                            <td>
                              <label className="lot-detail-modal-checkbox">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleDirectoryToggle(record.id, inLot)}
                                />
                              </label>
                              <div>{record.id}</div>
                              {renderSplitBadge(record)}
                            </td>
                            <td>{personalInfoRestricted ? '***' : (record?.name ?? '-')}</td>
                            <td>{typeof record.cardCount === 'number' ? record.cardCount.toLocaleString() : '-'}</td>
                            <td>{personalInfoRestricted ? '***' : (record.country ?? '-')}</td>
                            <td>{personalInfoRestricted ? '***' : (record.postalCode ?? '-')}</td>
                            <td>{personalInfoRestricted ? '***' : (record.addressJa ?? '-')}</td>
                            <td className="lot-detail-status-cell">{renderShipmentStatus(record.status)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="lot-detail-modal-selection">
                  <span>{pendingSummary}</span>
                  {removalIds.length > 0 && (
                    <span>{removalSummary}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="lot-detail-modal-footer">
              <button type="button" className="lot-detail-btn outline" onClick={closeAddModal}>
                {t('lotDetail.modal.actions.cancel')}
              </button>
              <button type="button" className="lot-detail-btn primary" disabled={!modalHasChanges} onClick={handleModalApply}>
                {t('lotDetail.modal.actions.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <div className="lot-detail-modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="lot-detail-modal lot-detail-help-modal" onClick={e => e.stopPropagation()}>
            <div className="lot-detail-modal-header">
              <h3>{t('lotDetail.help.title')}</h3>
              <button type="button" className="lot-detail-modal-close" onClick={() => setShowHelp(false)}>×</button>
            </div>
            <div className="lot-detail-help-body">
              <div className="lot-detail-help-flow">
                <h4>{t('lotDetail.help.flowTitle')}</h4>
                <ol className="lot-detail-help-steps">
                  <li>
                    <div className="lot-detail-help-step-badge step-create">{t('lotDetail.help.step1Badge')}</div>
                    <div>
                      <strong>{t('lotDetail.help.step1Title')}</strong>
                      <p>{t('lotDetail.help.step1Desc')}</p>
                    </div>
                  </li>
                  <li>
                    <div className="lot-detail-help-step-badge step-entry">{t('lotDetail.help.step2Badge')}</div>
                    <div>
                      <strong>{t('lotDetail.help.step2Title')}</strong>
                      <p>{t('lotDetail.help.step2Desc')}</p>
                    </div>
                  </li>
                  <li>
                    <div className="lot-detail-help-step-badge step-confirm">{t('lotDetail.help.step3Badge')}</div>
                    <div>
                      <strong>{t('lotDetail.help.step3Title')}</strong>
                      <p>{t('lotDetail.help.step3Desc')}</p>
                    </div>
                  </li>
                  <li>
                    <div className="lot-detail-help-step-badge step-delivery">{t('lotDetail.help.step4Badge')}</div>
                    <div>
                      <strong>{t('lotDetail.help.step4Title')}</strong>
                      <p>{t('lotDetail.help.step4Desc')}</p>
                    </div>
                  </li>
                  <li>
                    <div className="lot-detail-help-step-badge step-shipped">{t('lotDetail.help.step5Badge')}</div>
                    <div>
                      <strong>{t('lotDetail.help.step5Title')}</strong>
                      <p>{t('lotDetail.help.step5Desc')}</p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="lot-detail-help-section">
                <h4>ステータスについて</h4>

                <h5>WMSステータス</h5>
                <dl className="lot-detail-help-statuses">
                  <dt><span className="lot-detail-entry-badge unsent">未送信</span></dt>
                  <dd>WMSへの送信がまだです</dd>
                  <dt><span className="lot-detail-entry-badge sent">出庫指示済</span></dt>
                  <dd>出庫指示をWMSに送信しました</dd>
                  <dt><span className="lot-detail-entry-badge completed">出荷完了</span></dt>
                  <dd>WMSから出荷確認を受信しました</dd>
                </dl>

                <h5>入庫ステータス</h5>
                <dl className="lot-detail-help-statuses">
                  <dt><span className="lot-detail-entry-badge unsent">未送信</span></dt>
                  <dd>入庫予定がまだWMSに送信されていません</dd>
                  <dt><span className="lot-detail-entry-badge sent">送信済</span></dt>
                  <dd>入庫予定をWMSに送信しました</dd>
                  <dt><span className="lot-detail-entry-badge completed">入庫確認済</span></dt>
                  <dd>倉庫に到着し、検品・入庫処理が完了しました</dd>
                </dl>

                <h5>ロットステータス</h5>
                <p>仕分け中 → 仕分け済み → 発送準備完了 → 発送済み</p>
                <p>「発送済み」に変更する際はEMS追跡番号の入力が必須です。変更時にWMSへ商品同期+入庫予定が自動送信されます。</p>

                <h5>アラート</h5>
                <p>正常、注意、警告（WMS同期でエラーが発生した場合に設定されます）</p>
              </div>

              <div className="lot-detail-help-section">
                <h4>{getLabel('lot.guidelines.title', 'ロット追加/分離ルール')}</h4>
                <ul className="lot-detail-help-rules">
                  <li>
                    {isUnlimitedDays(guidelineNumbers.splitWindowDays)
                      ? getLabel('lot.guidelines.autoAddNoTimeLimit', '新着発送IDは「仕分け中」かつ累計{{cards}}枚以下のロットにのみ自動追加されます（期間制限なし）。', {
                          cards: formatGuidelineNumber(guidelineNumbers.maxCardsPerLot) ?? '-',
                        })
                      : getLabel('lot.guidelines.autoAdd', '新着発送IDは「仕分け中」で作成{{days}}日以内かつ累計{{cards}}枚以下のロットにのみ自動追加されます。', {
                          days: guidelineNumbers.splitWindowDays ?? '-',
                          cards: formatGuidelineNumber(guidelineNumbers.maxCardsPerLot) ?? '-',
                        })}
                  </li>
                  <li>
                    {getLabel('lot.guidelines.autoSplit', '上限を超えた発送IDは自動的に新規ロットへ分離されます（1回の一括追加上限は{{range}}件で、超過分は順次新規ロットになります）。', {
                      range: formatGuidelineNumber(guidelineNumbers.maxRangeSpan) ?? '-',
                    })}
                  </li>
                  <li>
                    {getLabel('lot.guidelines.statusRestriction', '{{shipped}}・{{ready}}のロットには発送IDを追加できません。必要な場合は新しいロットを作成してください。', {
                      shipped: t('lot.statuses.shipped'),
                      ready: t('lot.statuses.ready'),
                    })}
                  </li>
                </ul>
              </div>

              <div className="lot-detail-help-section">
                <h4>WMS連携について</h4>
                <ul className="lot-detail-help-rules">
                  <li>「発送済み」に変更すると、WMSへ商品同期と入庫予定が自動送信されます</li>
                  <li>入庫確認後、shipmentごとに個別の出庫指示（受取人情報・顧客ID付き）が自動送信されます</li>
                  <li>WMSから出荷確認を受信すると、各shipmentに中国国内の追跡番号が設定されます</li>
                  <li>ロットを削除するとWMSにキャンセルが送信されます</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LotDetail;
